import {
  useSalesServiceSalesOrdersPartialUpdate,
  useSalesServiceSalesProductsPriceCreate
} from '@insurance/api/queries'
import { EditGuarantees, Status9e7Enum } from '@insurance/api/requests'
import { useAppNavigate } from '@insurance/hooks'
import { paths } from '@insurance/routes'
import { ProductCode } from '@insurance/types'
import { replaceEmptyWithUndefined, showApiErrors } from '@shared/utils'
import { Button, Flex, Form } from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { useTranslation } from 'react-i18next'

import { GuaranteesFormValues, GuaranteesPriceValue } from './type'
import {
  formProps,
  normalizeEditData,
  normalizeGuaranteesData,
  removeGuaranteesValuesFromStorage,
  STEP
} from './utils'

type Props = {
  onReadyToSaveData?: () => void
}

export const GuaranteesInsuranceControls = ({ onReadyToSaveData }: Props) => {
  const { t } = useTranslation()
  const formInstance = Form.useFormInstance()
  const navigate = useAppNavigate()
  const { stepKey, priceKey, orderKey } = formProps(t)

  const step = Form.useWatch<number>(stepKey.name, { preserve: true })
  const orderId = Form.useWatch<number | undefined>(orderKey.name, {
    preserve: true
  })

  const hasPrevStep = step > STEP.GUARANTEE
  const isRegisterStep = step === STEP.PARTIES
  const isApproveStep = step === STEP.CUSTOMER && !!orderId
  const isNavigationStep = !isRegisterStep && !isApproveStep

  const handleError = (error: unknown) => showApiErrors(error, formInstance)

  const handleChangeStep = (newStep: number) => {
    formInstance.setFieldValue(stepKey.name, newStep)
    onReadyToSaveData?.()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const { mutate: updateOrder, isPending: isUpdateOrderPending } =
    useSalesServiceSalesOrdersPartialUpdate({
      onSuccess: (data, variable) => {
        const newStep = variable?.requestBody?.stage ?? STEP.GUARANTEE

        formInstance.setFieldValue(orderKey.name, data.id)

        const priceData = data?.price_data

        if (priceData) {
          const price: GuaranteesPriceValue = {
            amount: Number(Number(priceData.price).toFixed(2)),
            currency: priceData.currency,
            amountMdl: Number(Number(priceData.amount).toFixed(2)),
            exchangeRate: priceData.exchange_rate,
            exchangeRateDate: priceData.data?.exchange_rate_date,
            insuredAmountMdl: priceData.insured_amount_mdl
              ? Number(Number(priceData.insured_amount_mdl).toFixed(2))
              : undefined
          }

          formInstance.setFieldValue(priceKey.name, price)
        }

        handleChangeStep(newStep)
      },
      onError: (error: AnyObject) => {
        // The backend nests product errors under the product key; unwrap so
        // showApiErrors can map them onto the matching form fields.
        handleError({ ...error, body: error?.body?.guarantees ?? error?.body })
      }
    })

  const { mutate: register, isPending: isRegisterPending } =
    useSalesServiceSalesProductsPriceCreate({
      onSuccess: (data) => {
        if (!data.order?.id) return

        updateOrder({
          id: data.order.id,
          requestBody: {
            stage: STEP.MEDIA,
            status: Status9e7Enum.DATA_ENTRY
          }
        })
      },
      onError: handleError
    })

  const handlePrevStep = () => handleChangeStep(step - 1)

  const handleNavigate = async () => {
    await formInstance.validateFields()
    handleChangeStep(step + 1)
  }

  const handleRegister = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true) as GuaranteesFormValues

    // The draft already exists (the manager stepped back and returned).
    // Patching keeps a single order instead of registering a second one.
    if (values.order_id)
      return updateOrder({
        id: values.order_id,
        requestBody: {
          stage: STEP.MEDIA,
          status: Status9e7Enum.DATA_ENTRY,
          guarantees: replaceEmptyWithUndefined(
            normalizeEditData(values)
          ) as EditGuarantees
        }
      })

    register({
      code: ProductCode.GUARANTEES,
      requestBody: replaceEmptyWithUndefined(normalizeGuaranteesData(values))
    })
  }

  const handleReadyToApprove = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true) as GuaranteesFormValues

    if (!orderId) return

    updateOrder(
      {
        id: orderId,
        requestBody: {
          stage: STEP.CUSTOMER,
          status: Status9e7Enum.OFFER_READY,
          guarantees: replaceEmptyWithUndefined(
            normalizeEditData(values)
          ) as EditGuarantees
        }
      },
      {
        onSuccess: (order) => {
          removeGuaranteesValuesFromStorage(orderId)
          navigate(
            order?.id ? paths.orders.details(order.id) : paths.salesPanel.root
          )
        }
      }
    )
  }

  return (
    <Flex justify="end" gap={12}>
      {hasPrevStep && (
        <Button color="primary" variant="outlined" onClick={handlePrevStep}>
          {t('prev_step')}
        </Button>
      )}

      {isNavigationStep && (
        <Button type="primary" onClick={handleNavigate}>
          {t('next_step')}
        </Button>
      )}

      {isRegisterStep && (
        <Button
          type="primary"
          loading={isRegisterPending || isUpdateOrderPending}
          onClick={handleRegister}
        >
          {t('register_guarantee')}
        </Button>
      )}

      {isApproveStep && (
        <Button
          type="primary"
          loading={isUpdateOrderPending}
          onClick={handleReadyToApprove}
        >
          {t('to_approve')}
        </Button>
      )}
    </Flex>
  )
}
